import { Button, Col, Flex, Tooltip } from 'antd';
import React from 'react';
import { LogiblocksIcon } from '../../../assets/icons/icons';
import { FileDownload } from '../../../assets/images';
import { truncateByLength } from '../../../common/helper';
import { CustomParagraph, CustomShipmentText } from '../../../components';
import { AlertNestedCoCNote } from '@/apis/types';
import { COCFormattedTime } from './OneTimeCOCAlert';

interface AlertNestedCoCNoteRowProps {
  data: AlertNestedCoCNote;
  getFormattedTime: (timestamp: string) => COCFormattedTime;
  showFull: boolean;
  setShowFull: React.Dispatch<React.SetStateAction<boolean>>;
  /** Downloads one of the note's files (the timeline's, as for top-level notes). */
  downloadFile: (fileUrl: string, fileName: string) => void;
}

/** A Logiblocks CoC note, shown inside the alert that triggered its workflow. */
const AlertNestedCoCNoteRow: React.FC<AlertNestedCoCNoteRowProps> = ({
  data,
  getFormattedTime,
  showFull,
  setShowFull,
  downloadFile,
}) => {
  const title = data.title || 'Automation note';

  return (
    <Flex
      gap={12}
      style={{
        marginTop: '12px',
        marginBottom: '12px',
      }}
    >
      {/* Same icon column as the alert's other entries (CoC_CT), so every row's
          text starts at the same place. */}
      <Col
        span={1}
        style={{
          display: 'flex',
          justifyContent: 'start',
          marginRight: '20px',
        }}
      >
        <Flex
          align='center'
          justify='center'
          data-testid='alert-nested-coc-note-icon'
          style={{
            backgroundColor: 'var(--gray-600)',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            flexShrink: 0,
          }}
        >
          <LogiblocksIcon size={16} color='white' />
        </Flex>
      </Col>
      <Flex vertical justify='space-between' style={{ width: '100%' }}>
        <Flex justify='space-between' style={{ height: '100%' }}>
          <Tooltip title={title}>
            <Flex align='center' style={{ height: '100%' }}>
              <CustomParagraph
                text={title}
                fontSize='12px'
                fontWeight={600}
                lineHeight='16px'
                color='var(--text-primary)'
                marginBottom={0}
                whiteSpace='pre-line'
              />
            </Flex>
          </Tooltip>
          {data.timestamp && (
            <Flex
              align='center'
              onClick={() => setShowFull((prev) => !prev)}
              style={{
                cursor: 'pointer',
                height: '100%',
              }}
            >
              <CustomParagraph
                text={
                  showFull
                    ? getFormattedTime(data.timestamp)?.full
                    : getFormattedTime(data.timestamp)?.relative
                }
                fontSize='12px'
                fontWeight={400}
                lineHeight='16px'
                color='var(--text-light)'
                marginBottom='0'
                style={{ whiteSpace: 'nowrap' }}
              />
            </Flex>
          )}
        </Flex>
        <div
          className='reply-comment-message mt-2'
          dangerouslySetInnerHTML={{ __html: data.message }}
        />
        {data.attachments?.length ? (
          <Flex wrap data-testid='alert-nested-coc-note-attachments'>
            {data.attachments.map((item) => (
              <Flex vertical key={`${item.file_url}-${item.file_name}`}>
                <Button
                  type='default'
                  className='zl-btn'
                  aria-label={`Download ${item.file_name}`}
                  onClick={() => downloadFile(item.file_url, item.file_name)}
                >
                  <img
                    src={FileDownload}
                    width={16}
                    alt=''
                    className='zl-invert-on-dark'
                  />
                </Button>
                <CustomShipmentText
                  text={truncateByLength(item.file_name, 6)}
                  fontSize='10px'
                  color='var(--text-secondary)'
                  marginBottom='0'
                />
              </Flex>
            ))}
          </Flex>
        ) : null}
      </Flex>
    </Flex>
  );
};

export default AlertNestedCoCNoteRow;
